import React, { useState } from 'react';
import { Fab, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { PiQuestion, PiX } from 'react-icons/pi';

interface HelpTextObject {
  overview?: string;
  tips?: string[];
}

interface WorkflowHelpProps {
  practice: string;
  workflow: string;
  helpText?: string | HelpTextObject; // V2 recipe help text or UI schema help_text (can be string or object)
}

// Legacy static instructions - fallback only when no help_text in recipe or UI schema
// NOTE: These should be migrated to ui-schema.json help_text property
const legacyInstructions: Record<string, Record<string, string>> = {
  'patent': {
    'rfa': `# Requests for Admission (RFA)

## Overview
Generate patent-specific requests for admission in litigation proceedings.

## Instructions
1. **Upload Documents**: Upload complaint, patent claims, or prior art documents
2. **Review Generated Requests**: The AI will analyze allegations and generate targeted RFAs
3. **Customize Output**: Edit and refine the generated requests as needed

## Tips
- Include detailed patent claims for better analysis
- Upload relevant prior art for comprehensive coverage
- Review generated requests for accuracy and completeness`,
    
    'rfp': `# Requests for Production (RFP)

## Overview
Generate patent-specific requests for production of documents.

## Instructions
1. **Upload Case Documents**: Include complaint, patents, and technical specifications
2. **AI Analysis**: System identifies key technical areas and potential evidence
3. **Document Requests**: Generates targeted requests for technical documents

## Best Practices
- Include technical specifications when available
- Upload complete patent portfolio for comprehensive analysis
- Review requests for scope and relevance`,
    
    'mtd': `# Motion to Dismiss

## Overview
Draft Section 101 motion to dismiss for patent invalidity under Alice/Mayo framework.

## Instructions
1. **Upload Patent Claims**: Provide complete patent claims and specifications
2. **Alice Analysis**: AI performs two-step Alice analysis
3. **Brief Generation**: Creates motion with supporting arguments

## Key Points
- Focus on abstract ideas and lack of inventive concept
- Include relevant case law citations
- Review generated arguments for strength and applicability`
  }
};

export default function WorkflowHelp({ practice, workflow, helpText }: WorkflowHelpProps) {
  const [open, setOpen] = useState(false);

  // Handle help_text as string or object { overview, tips }
  const getInstructionsFromHelpText = (ht: string | HelpTextObject | undefined): string | null => {
    if (!ht) return null;
    if (typeof ht === 'string') return ht;
    if (typeof ht === 'object') {
      // Build instructions from object format
      let result = ht.overview || '';
      if (ht.tips && Array.isArray(ht.tips) && ht.tips.length > 0) {
        result += '\n\n## Tips\n' + ht.tips.map(tip => `- ${tip}`).join('\n');
      }
      return result || null;
    }
    return null;
  };

  // Priority: 1) helpText prop (from recipe/ui-schema), 2) legacy static, 3) generic default
  const instructions = getInstructionsFromHelpText(helpText) || legacyInstructions[practice]?.[workflow] || `# ${workflow.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}

No specific instructions available for this workflow yet.

## General Steps
1. Upload your documents using the file uploader
2. Configure any required workflow parameters
3. Submit the job and monitor progress
4. Download results when processing is complete`;

  return (
    <>
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
        onClick={() => setOpen(true)}
      >
        <PiQuestion size={24} />
      </Fab>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Workflow Instructions
          </Typography>
          <Button
            onClick={() => setOpen(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <PiX size={24} />
          </Button>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mb: 2 }, '& h2': { fontSize: '1.25rem', fontWeight: 'bold', mb: 1, mt: 2 }, '& p': { mb: 1 }, '& ul': { pl: 2, mb: 2 }, '& li': { mb: 0.5 } }}>
            {instructions.split('\n').map((line, index) => {
              if (line.startsWith('# ')) {
                return <Typography key={index} variant="h4" sx={{ mb: 2 }}>{line.substring(2)}</Typography>;
              } else if (line.startsWith('## ')) {
                return <Typography key={index} variant="h6" sx={{ mb: 1, mt: 2 }}>{line.substring(3)}</Typography>;
              } else if (line.startsWith('- ')) {
                return <Typography key={index} component="li" sx={{ ml: 2, mb: 0.5 }}>{line.substring(2)}</Typography>;
              } else if (line.trim()) {
                return <Typography key={index} sx={{ mb: 1 }}>{line}</Typography>;
              }
              return <Box key={index} sx={{ height: 8 }} />;
            })}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}